// A real league's direct conversations, in the chat drawer (scenario I8,
// PLAN-2026-10-18 §10.5): the list of mine with a dot on a new one, whom I
// can begin one with, and one conversation open with Back. Only its two
// people read a conversation; the commissioner has no view of it.
//
// Clean (LOOKS.md Part 7): drawn through the kit, so a look restyles it.

import { useState } from 'react';
import { whenLabel } from '../data/notifications';
import { useConversationFeed } from '../data/chat';
import { Button, Dot, Heading, ListRow, RowName, RowSub, tell } from '../kit/ui';
import { Channel } from './ChatPanel';
import { useChatDrawer, type ChatView } from './chatDrawer';

export function ConversationList() {
  const { feed, fresh, setView, message } = useChatDrawer();
  const [opening, setOpening] = useState<string | null>(null);
  const conversations = feed.conversations;
  const now = Date.now();
  if (conversations === undefined)
    return <div className="fsp-chat-list fsp-empty">Reading your messages.</div>;
  const talking = new Set(conversations.map((c) => c.with.teamId).filter((t) => t !== null));
  const others = (feed.people ?? []).filter((p) => !talking.has(p.teamId));
  const begin = async (teamId: string) => {
    setOpening(teamId);
    const done = await message(teamId);
    setOpening(null);
    if (!done.ok) tell(done.message, 'bad');
  };
  return (
    <div className="fsp-chat-list">
      {conversations.length === 0 && others.length === 0 ? (
        <div className="fsp-empty">
          Nobody else runs a team here yet. Once somebody does, you can write to them here.
        </div>
      ) : null}
      {conversations.map((c) => {
        const isNew = fresh.has(c.channel);
        return (
          <ListRow
            key={c.channel}
            tall
            lead={<Dot on={isNew} />}
            leadSize="sm"
            trail={
              c.last ? <span className="fsp-sub">{whenLabel(c.last.at, now)}</span> : undefined
            }
            onClick={() => setView(c.channel as ChatView)}
          >
            <RowName marks={isNew ? <span className="fsp-chat-new">New</span> : undefined}>
              {c.with.name}
            </RowName>
            <RowSub>
              {c.last === null
                ? (c.with.teamName ?? 'Nothing written yet.')
                : `${c.last.mine ? 'You: ' : ''}${c.last.body}`}
            </RowSub>
          </ListRow>
        );
      })}
      {others.length > 0 ? (
        <>
          <div className="fsp-chat-list-head">
            <Heading>Write to</Heading>
          </div>
          {others.map((p) => (
            <ListRow
              key={p.teamId}
              trail={
                <Button
                  size="xs"
                  variant="quiet"
                  icon="chat"
                  loading={opening === p.teamId}
                  label={`Message ${p.name}`}
                  onClick={() => void begin(p.teamId)}
                >
                  Message
                </Button>
              }
            >
              <RowName>{p.name}</RowName>
              <RowSub>{p.teamName}</RowSub>
            </ListRow>
          ))}
        </>
      ) : null}
    </div>
  );
}

export function ConversationPanel({ channel }: { channel: `dm:${string}` }) {
  const { feed: league, setView } = useChatDrawer();
  const feed = useConversationFeed(channel);
  const ref = league.conversations?.find((c) => c.channel === channel) ?? null;
  const who = ref?.with.name ?? 'them';
  return (
    <div className="fsp-chat-convo">
      <div className="fsp-chat-convo-head">
        <Button
          size="xs"
          variant="quiet"
          icon="back"
          label="Back to messages"
          onClick={() => setView('messages')}
        >
          Back
        </Button>
        <span className="fsp-chat-convo-who">
          <span className="fsp-name-text">{who}</span>
          {ref?.with.teamName ? <span className="fsp-sub"> · {ref.with.teamName}</span> : null}
        </span>
      </div>
      <div className="fsp-chat-room">
        <Channel compact={false} starters={NO_STARTERS} feed={feed} conversationWith={who} />
      </div>
    </div>
  );
}

const NO_STARTERS: string[] = [];
